import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import EventImage from "@/components/EventImage";
import { useLang } from "@/context/LanguageContext";
import { Hammer, Music, Drama, Users, CalendarDays, Sparkles, Megaphone, MapPin, ArrowRight, Tent } from "lucide-react";

const iconFor = (type) => (type === "band" ? Music : type === "troupe" ? Drama : Hammer);

export default function ClanHighlight({ limit = 6 }) {
  const { t } = useLang();
  const { data: pros = [] } = useQuery({
    queryKey: ["clanFeatured", limit],
    queryFn: async () => (await api.get("/clan/featured", { params: { limit } })).data,
  });
  if (!pros.length) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16" data-testid="home-clan">
      <div className="flex items-end justify-between gap-4 mb-8 border-b border-amber-900/30 pb-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> {t("directory_title")}
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-100">{t("clan_home_title")}</h2>
          <p className="text-stone-400 mt-2 text-sm sm:text-base max-w-2xl">{t("clan_home_subtitle")}</p>
        </div>
        <Link to="/directory" data-testid="home-clan-all" className="font-mono text-xs uppercase tracking-wider text-amber-500 hover:text-amber-400 flex items-center gap-1.5 shrink-0">{t("view_all")} <ArrowRight className="w-3.5 h-3.5" /></Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {pros.map((e, i) => {
          const Icon = iconFor(e.type);
          const boosted = !!e.featured;
          const sponsored = e.feature_type === "sponsored";
          return (
            <motion.div key={`${e.source}-${e.slug}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}>
              <Link to={e.profile_path} data-testid={`home-clan-${e.slug}`}
                className={`group relative flex flex-col h-full rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 ${
                  boosted ? "bg-gradient-to-br from-[#181A20] via-[#121418] to-[#1E1710] border-2 border-amber-500/50 ember-glow" : "bg-[#121418] border border-[#272A34] hover:border-amber-500/50"}`}>
                {boosted && (
                  <span className={`absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest font-bold ${sponsored ? "bg-stone-800/90 text-stone-200 border border-stone-600" : "bg-amber-500 text-stone-950 border border-amber-300"}`}>
                    {sponsored ? <Megaphone className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                    {sponsored ? t("dir_sponsored") : t("dir_discover")}
                  </span>
                )}
                <div className="relative h-44 overflow-hidden bg-stone-900">
                  <EventImage src={e.image} alt={e.name} imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/30 to-transparent opacity-90" />
                  {e.has_historical_tent && <span data-testid={`home-clan-tent-${e.slug}`} className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase bg-amber-950/80 text-amber-200 border border-amber-800/50 backdrop-blur-sm"><Tent className="w-3 h-3" /> Tente</span>}
                  {e.craft && (
                    <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono uppercase bg-amber-950/80 text-amber-300 border border-amber-800/50 backdrop-blur-sm">
                      <Icon className="w-3.5 h-3.5" /> {e.craft}
                    </span>
                  )}
                  {e.upcoming_events > 0 && (
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono bg-emerald-950/80 text-emerald-300 border border-emerald-700/50 backdrop-blur-sm">
                      <CalendarDays className="w-3 h-3" /> {e.upcoming_events} {t("dir_coming")}
                    </span>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-serif text-lg text-stone-100 group-hover:text-amber-200 transition-colors mb-1.5 line-clamp-1">{e.name}</h3>
                  <p className="text-sm text-stone-400 line-clamp-2 leading-relaxed mb-3">{e.bio}</p>
                  <div className="mt-auto flex items-center gap-1.5 text-xs text-stone-500 font-mono uppercase tracking-wider">
                    <MapPin className="w-3.5 h-3.5 text-amber-600/80" /> {e.region || "Suisse"}
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
